import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AngularSvgIconModule, SvgIconComponent } from 'angular-svg-icon';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { FeedbackStepData } from '../../feedback-step.interface';
import { FeedbackQuestion } from '../../../feedback';
import { QuestionType, QUESTION_TYPE_MAP } from '../../feedback-question.enum';
import { SubQuestionCategory } from '../../../feeback.enum';
import { Subject } from 'rxjs';
import { CaseDetailModel } from '../../../../../insights.interface';

@Component({
  selector: 'app-self-assessment-step',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NzFormModule,
    NzRadioModule,
    NzButtonModule,
    NzFormModule,
    NzDividerModule,
    NzInputModule,
    NzTabsModule,
    NzCollapseModule,
    SvgIconComponent,
    AngularSvgIconModule,
  ],
  templateUrl: './self-assessment-step.component.html',
  styleUrls: ['../../feedback-form.component.scss'],
})
export class SelfAssessmentStepComponent implements OnChanges, OnDestroy {
  @Input() caseDetails: CaseDetailModel | null = null;
  @Input() stepData!: FeedbackStepData;
  @Input() formGroup!: FormGroup;
  @Input() onNext!: () => void;
  @Input() onPrevious!: () => void;
  @Input() onSubmit!: () => void;
  @Input() overAllScore = 0;

  private readonly destroy$ = new Subject<void>();
  private readonly textAnswerMaxLength = 500;

  selectedTabIndex = 0;

  tabs = [
    { title: 'Teacher Only', key: 'teacher' },
    { title: 'Student Only', key: 'student' },
    { title: 'Both', key: 'both' },
  ];

  teacherQuestions: FeedbackQuestion[] = [];
  studentQuestions: FeedbackQuestion[] = [];
  bothQuestions: FeedbackQuestion[] = [];
  questionIndexMap = new Map<number, number>();
  teacherQuestionsWithIndex: {
    question: FeedbackQuestion;
    index: number;
  }[] = [];
  studentQuestionsWithIndex: {
    question: FeedbackQuestion;
    index: number;
  }[] = [];
  bothQuestionsWithIndex: {
    question: FeedbackQuestion;
    index: number;
    isTextQuestion: boolean;
  }[] = [];

  textQuestionIds = new Set<number>();

  sections = [
    {
      active: true,
      name: "Teacher's Assessment",
    },
    {
      active: true,
      name: "Student's Assessment",
    },
  ];

  get studentFullName(): string {
    const firstName = this.caseDetails?.student?.firstName?.trim() ?? '';
    const lastName = this.caseDetails?.student?.lastName?.trim() ?? '';
    return `${firstName} ${lastName}`.trim();
  }

  formatTeacherQuestion(question: string): string {
    const normalizedQuestion = question?.trim() ?? '';
    if (!normalizedQuestion) {
      return this.studentFullName;
    }

    const sentenceQuestion =
      normalizedQuestion.charAt(0).toLowerCase() + normalizedQuestion.slice(1);

    return this.studentFullName
      ? `${this.studentFullName} ${sentenceQuestion}`
      : sentenceQuestion;
  }

  ngOnChanges() {
    if (!this.stepData) {
      console.error('stepData is not provided to SelfAssessmentStepComponent');
      return;
    }

    if (!this.stepData.questions) {
      console.error('stepData.questions is not available');
      return;
    }

    if (!this.formGroup) {
      console.error('formGroup is not provided to SelfAssessmentStepComponent');
      return;
    }

    this.initializeQuestionData();
    this.setupTextQuestionValidation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeQuestionData() {
    this.teacherQuestions = this.stepData.questions.filter(
      (q: FeedbackQuestion) =>
        q.subCategoryId === SubQuestionCategory.TeacherReflection
    );

    this.studentQuestions = this.stepData.questions.filter(
      (q: FeedbackQuestion) =>
        q.subCategoryId === SubQuestionCategory.StudentSelfAssessment
    );

    this.bothQuestions = this.stepData.questions.filter(
      (q: FeedbackQuestion) =>
        q.subCategoryId === SubQuestionCategory.TeacherReflection ||
        q.subCategoryId === SubQuestionCategory.StudentSelfAssessment
    );

    this.textQuestionIds.clear();
    this.stepData.questions.forEach(question => {
      const questionType = QUESTION_TYPE_MAP[question.id];
      if (questionType === QuestionType.Text) {
        this.textQuestionIds.add(question.id);
      }
    });

    const questionResponses = this.formGroup.get(
      'questionResponses'
    ) as FormArray;
    questionResponses.controls.forEach((control, index) => {
      const questionId = control.get('caseFeedbackQuestionId')?.value;
      if (questionId) {
        this.questionIndexMap.set(questionId, index);
      }
    });

    this.teacherQuestionsWithIndex = this.teacherQuestions
      .map(question => ({
        question,
        index: this.questionIndexMap.get(question.id) ?? -1,
      }))
      .filter(item => item.index >= 0);

    this.studentQuestionsWithIndex = this.studentQuestions
      .map(question => ({
        question,
        index: this.questionIndexMap.get(question.id) ?? -1,
      }))
      .filter(item => item.index >= 0);

    this.bothQuestionsWithIndex = this.bothQuestions
      .map(question => ({
        question,
        index: this.questionIndexMap.get(question.id) ?? -1,
        isTextQuestion: this.textQuestionIds.has(question.id),
      }))
      .filter(item => item.index >= 0);
  }

  private setupTextQuestionValidation(): void {
    const questionResponses = this.formGroup.get(
      'questionResponses'
    ) as FormArray;

    this.textQuestionIds.forEach(questionId => {
      const index = this.getQuestionIndex(questionId);
      if (index < 0) {
        return;
      }

      const control = questionResponses.at(index);
      const descriptionControl = control.get('description');
      if (!descriptionControl) {
        return;
      }

      descriptionControl.setValidators([
        Validators.maxLength(this.textAnswerMaxLength),
      ]);
      descriptionControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  get q9Index(): number {
    return this.questionIndexMap.get(9) ?? -1;
  }

  get q10Index(): number {
    return this.questionIndexMap.get(10) ?? -1;
  }

  getQuestionValidationStatus(questionId: number): string {
    const index = this.getQuestionIndex(questionId);
    if (index >= 0) {
      const questionResponses = this.formGroup.get(
        'questionResponses'
      ) as FormArray;
      const control = questionResponses.at(index);

      let invalidControl;
      if (this.isTextQuestion(questionId)) {
        invalidControl = control.get('description');
        // Only show maxLength error if not empty (required takes precedence)
        if (
          invalidControl?.errors?.['maxlength'] &&
          invalidControl?.value &&
          (invalidControl?.dirty || invalidControl?.touched)
        ) {
          return 'error';
        }
      } else {
        invalidControl = control.get('ratingScale');
        if (
          invalidControl?.invalid &&
          (invalidControl?.dirty || invalidControl?.touched)
        ) {
          return 'error';
        }
      }
    }
    return '';
  }

  getQuestionErrorTip(questionId: number): string {
    const control = this.getQuestionControl(questionId);
    if (!control) {
      return '';
    }

    const fieldControl = this.getFieldControl(control, questionId);
    return this.getControlError(fieldControl);
  }

  private getQuestionControl(questionId: number): FormGroup | null {
    const index = this.getQuestionIndex(questionId);
    if (index < 0) {
      return null;
    }
    const questionResponses = this.formGroup.get(
      'questionResponses'
    ) as FormArray;
    return questionResponses.at(index) as FormGroup;
  }

  private getFieldControl(
    control: FormGroup,
    questionId: number
  ): AbstractControl | null {
    const fieldName = this.isTextQuestion(questionId)
      ? 'description'
      : 'ratingScale';
    return control.get(fieldName);
  }

  private getControlError(control: AbstractControl | null): string {
    if (!control) {
      return '';
    }
    const { invalid, dirty, touched, errors } = control;
    if (!invalid || (!dirty && !touched)) {
      return '';
    }

    if (errors?.['required']) {
      return 'Answer is required';
    }

    if (errors?.['maxlength']) {
      return `Answer can not exceed ${this.textAnswerMaxLength} characters.`;
    }

    return '';
  }

  getTextAnswerLength(questionId: number): number {
    const questionControl = this.getQuestionControl(questionId);
    const description = questionControl?.get('description')?.value;
    return typeof description === 'string' ? description.length : 0;
  }

  getQuestionIndex(questionId: number): number {
    return this.questionIndexMap.get(questionId) ?? -1;
  }

  getTeacherQuestions(): FeedbackQuestion[] {
    return this.teacherQuestions;
  }

  getStudentQuestions(): FeedbackQuestion[] {
    return this.studentQuestions;
  }

  getBothQuestions(): FeedbackQuestion[] {
    return this.bothQuestions;
  }

  getQuestionType(questionId: number): QuestionType {
    return QUESTION_TYPE_MAP[questionId] || QuestionType.Scale;
  }

  isTextQuestion(questionId: number): boolean {
    return this.textQuestionIds.has(questionId);
  }

  trackByQuestionId(
    index: number,
    questionItem: { question: FeedbackQuestion; index: number }
  ): number {
    return questionItem.question.id;
  }

  onSectionChange(index: number, isActive: boolean): void {
    this.sections[index].active = isActive;
  }

  getScaleOptions() {
    return [
      { label: 'Never', value: 1 },
      { label: 'Sometimes', value: 2 },
      { label: 'Often', value: 3 },
      { label: 'Almost Always', value: 4 },
    ];
  }
}
